/**
 * PATH: packages/ui/src/kanban/components/KanbanCardPeekComments.tsx
 * FILE_ID: UI-KANBAN-CARD-PEEK-COMMENTS-001
 * PURPOSE: A compact human-comment feed + composer at the foot of the AI thread
 *   peek so the operator can discuss a card without opening the full drawer.
 * WHY:
 *   FEAT-2026-06-21-KANBAN-CARD-PEEK-RICHEN — operator: "I can't add comments
 *   underneath and such on the threads". The peek surfaced the AI summary +
 *   email thread read-only; this closes the collaboration loop in-place. Shares
 *   the drawer's `['kanban','card',id,'comments']` query key so a comment added
 *   here shows in the drawer (and vice-versa) with no extra fetch.
 * FLOW:
 *   ┌──────────────┐  Cmd+Enter  ┌──────────────────────┐   ┌──────────────┐
 *   │ composer     │ ──────────▶ │ createComment(id,body)│ → │ optimistic   │
 *   │ textarea     │             │ + invalidate feed     │   │ list refresh │
 *   └──────────────┘             └──────────────────────┘   └──────────────┘
 * DEPENDENCIES:
 *   - @tanstack/react-query: feed query + post/delete mutations.
 *   - ./KanbanAvatar: author avatar (resolved via resolveMember).
 *   - ../api KanbanApi (listComments / createComment / deleteComment).
 * RELATED:
 *   - ./card-detail/CommentsSection.tsx: the drawer's fuller feed (same key).
 *   - ./KanbanCardPeek.tsx: renders this as the peek's last section.
 */
import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, MessageSquare, Send, X } from 'lucide-react'

import type { KanbanApi, KanbanMember } from '../api'
import { KanbanAvatar } from './KanbanAvatar'
import { CommentBody } from './comment-text'
import { cardCommentsQueryKey } from './peekQueryKeys'
import { resolveCommentAuthorLabel } from './card-detail/commentAuthor'
import {
  commentDraftScope,
  migrateAnonCommentDraft,
  readCommentDraft,
  useCommentDraft,
} from './card-detail/commentDraft'

export interface KanbanCardPeekCommentsProps {
  cardId: string
  api: Pick<KanbanApi, 'listComments' | 'createComment' | 'deleteComment'>
  resolveMember?: (userId: number) => KanbanMember | undefined
  /** Scope the card-list cache bust to this board when known; falls back to a
   *  broad `['kanban','cards']` invalidate so the card-face comment count still
   *  refreshes on hosts that don't thread a board id. */
  boardId?: string
  /** Current viewer id — scopes the LOCAL comment draft so it is never visible
   *  to another user on a shared profile. Absent → device-scoped 'anon' draft
   *  (still restorable, never dead). */
  currentUserId?: string | number | null
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const now = Date.now()
  const diffMin = Math.round((now - d.getTime()) / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m`
  if (diffMin < 1440) return `${Math.round(diffMin / 60)}h`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** Compact comments feed for the peek. Read + write share the drawer's query
 *  key so the two surfaces stay in lock-step without a second round-trip. */
export function KanbanCardPeekComments({
  cardId,
  api,
  resolveMember,
  boardId,
  currentUserId,
}: KanbanCardPeekCommentsProps) {
  const qc = useQueryClient()
  // Local-only draft (per-user + per-card). Seed the composer from any saved
  // draft so a half-typed comment survives a hover-away / remount; mirror every
  // keystroke into the store and purge on a successful send.
  const { draft, setDraft, clearDraft } = useCommentDraft(currentUserId, cardId)
  const [body, setBody] = useState(draft)
  // Stale-body guard: this composer instance can be REUSED for a different card
  // or after anon→realId hydrate. Re-seed from the saved draft when the draft
  // scope changes — but ONLY on change, so we never clobber active typing for
  // the SAME card+user. (React "adjust state during render" pattern.)
  const draftScope = commentDraftScope(currentUserId, cardId)
  const lastDraftScopeRef = useRef(draftScope)
  if (lastDraftScopeRef.current !== draftScope) {
    lastDraftScopeRef.current = draftScope
    migrateAnonCommentDraft(currentUserId, cardId)
    setBody(readCommentDraft(currentUserId, cardId))
  }

  const q = useQuery({
    queryKey: cardCommentsQueryKey(cardId),
    queryFn: () => api.listComments(cardId),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: cardCommentsQueryKey(cardId) })
    qc.invalidateQueries({ queryKey: boardId ? ['kanban', 'cards', boardId] : ['kanban', 'cards'] })
  }

  const post = useMutation({
    mutationFn: (text: string) => api.createComment(cardId, text),
    onSuccess: () => {
      setBody('')
      clearDraft()
      invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteComment(id),
    onSuccess: invalidate,
  })

  const comments = q.data ?? []

  return (
    <section className="border-t border-neutral-100 pt-2 dark:border-zinc-800">
      {/* "Card notes" mirrors the drawer's legacy comment section label
          (shared ['kanban','card',id,'comments'] query key) so hovering the
          Thread chip shows the SAME notes the card-face badge counts and the
          drawer lists (BUG-2026-07-02-KANBAN-COMMENTS-INVISIBLE). */}
      <p className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
        <MessageSquare className="h-3 w-3" aria-hidden /> Card notes
        {comments.length > 0 && <span className="text-neutral-400">· {comments.length}</span>}
      </p>

      {/* Composer first — the operator's primary intent here is to ADD. */}
      <div className="mb-2">
        <textarea
          value={body}
          onChange={(e) => {
            setBody(e.target.value)
            setDraft(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && body.trim()) {
              e.preventDefault()
              post.mutate(body.trim())
            }
          }}
          rows={2}
          placeholder="Add a comment… (⌘↵ to send)"
          className="w-full resize-none rounded-md border border-neutral-200 bg-white px-2 py-1.5 text-[12px] text-neutral-800 placeholder:text-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
        />
        <div className="mt-1 flex items-center justify-end gap-2">
          {/* Manual draft purge — shown only when there's something to clear so
              it never crowds the composer at rest. */}
          {body.trim() && (
            <button
              type="button"
              onClick={() => {
                setBody('')
                clearDraft()
              }}
              className="text-[11px] font-medium text-neutral-400 transition-colors hover:text-neutral-600 dark:hover:text-zinc-300"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={() => body.trim() && post.mutate(body.trim())}
            disabled={!body.trim() || post.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            {post.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            Comment
          </button>
        </div>
      </div>

      {q.isLoading && <p className="py-1 text-[11px] text-neutral-400">Loading comments…</p>}
      {!q.isLoading && comments.length === 0 && (
        <p className="py-1 text-[11px] italic text-neutral-400">No comments yet — start the thread.</p>
      )}

      <ul className="space-y-1.5">
        {comments
          .slice()
          .reverse()
          .map((c) => {
            const member = c.author != null ? resolveMember?.(c.author) : undefined
            const label = resolveCommentAuthorLabel(c, resolveMember)
            return (
              <li key={c.id} className="group/comment flex gap-2">
                {member ? (
                  <KanbanAvatar member={member} size={20} className="mt-0.5 shrink-0" />
                ) : (
                  <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-[9px] font-semibold text-neutral-500 dark:bg-zinc-700 dark:text-zinc-300">
                    {label.slice(0, 1).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="truncate text-[11px] font-semibold text-neutral-700 dark:text-zinc-200">
                      {label}
                    </span>
                    <span className="shrink-0 text-[10px] text-neutral-400">
                      {formatWhen(c.created_at)}
                      {c.edited_at ? ' · edited' : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => remove.mutate(c.id)}
                      aria-label="Delete comment"
                      title="Delete comment"
                      className="ml-auto shrink-0 rounded p-0.5 text-neutral-300 opacity-0 transition-opacity hover:bg-rose-50 hover:text-rose-600 group-hover/comment:opacity-100 dark:text-zinc-600 dark:hover:bg-rose-950/40"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  <p className="text-[12px] leading-snug text-neutral-700 dark:text-zinc-200">
                    <CommentBody text={c.body} />
                  </p>
                </div>
              </li>
            )
          })}
      </ul>
    </section>
  )
}
